import { useState, type FormEvent } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Modal } from '../../components/Modal';
import { getErrorMessage } from '../../lib/errors';
import { formatDate } from '../../lib/format';
import type { CampaignCreate, CampaignRead, CampaignUpdate } from '../../types/api';
import { CAMPAIGN_COLORS, DEFAULT_CAMPAIGN_COLOR } from './campaignColors';

interface CampaignFormProps {
  initial: CampaignRead | null;
  isSaving: boolean;
  error: string | null;
  onSubmit: (body: CampaignCreate) => void;
  onCancel: () => void;
}

function CampaignForm({ initial, isSaving, error, onSubmit, onCancel }: CampaignFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [color, setColor] = useState(initial?.color ?? DEFAULT_CAMPAIGN_COLOR);
  const [startsOn, setStartsOn] = useState(initial?.starts_on ?? '');
  const [endsOn, setEndsOn] = useState(initial?.ends_on ?? '');
  const [fieldError, setFieldError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!name.trim()) {
      setFieldError('Name the campaign before saving.');
      return;
    }
    if (startsOn && endsOn && endsOn < startsOn) {
      setFieldError('The end date cannot fall before the start date.');
      return;
    }
    setFieldError(null);
    onSubmit({
      name: name.trim(),
      color,
      description: initial?.description ?? null,
      starts_on: startsOn || null,
      ends_on: endsOn || null,
    });
  }

  const message = fieldError ?? error;

  return (
    <form className="form campaign-form" onSubmit={handleSubmit} noValidate>
      <label className="field">
        <span className="field__label">Name</span>
        <input
          className="field__input"
          type="text"
          maxLength={120}
          placeholder="Spring launch"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>

      <fieldset className="kb-fieldset">
        <legend className="field__label">Colour</legend>
        <div className="swatch-picker">
          {CAMPAIGN_COLORS.map((option) => (
            <label
              className={option.value === color ? 'swatch swatch--selected' : 'swatch'}
              key={option.value}
            >
              <input
                className="visually-hidden"
                type="radio"
                name="campaign-colour"
                value={option.value}
                checked={option.value === color}
                onChange={() => setColor(option.value)}
              />
              <span className="swatch__dot" style={{ backgroundColor: option.value }} />
              <span className="visually-hidden">{option.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="kb-form__row">
        <label className="field">
          <span className="field__label">Starts on</span>
          <input
            className="field__input"
            type="date"
            value={startsOn}
            onChange={(event) => setStartsOn(event.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">Ends on</span>
          <input
            className="field__input"
            type="date"
            value={endsOn}
            onChange={(event) => setEndsOn(event.target.value)}
          />
          <span className="field__hint">Both optional.</span>
        </label>
      </div>

      {message ? (
        <p className="form__error" role="alert">
          {message}
        </p>
      ) : null}

      <div className="kb-form__actions">
        <button className="button" type="submit" disabled={isSaving}>
          {isSaving ? 'Saving' : 'Save campaign'}
        </button>
        <button
          className="button button--ghost"
          type="button"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

interface CampaignManagerProps {
  campaigns: readonly CampaignRead[];
  onCreate: (body: CampaignCreate) => Promise<unknown>;
  onUpdate: (id: string, body: CampaignUpdate) => Promise<unknown>;
  onRemove: (id: string) => Promise<void>;
  onDismiss: () => void;
}

/**
 * Campaigns, out of the way until asked for.
 *
 * They are a grouping and a colour, nothing more — no pipeline stage reads them
 * — so they live behind one control on the toolbar rather than on the screen.
 *
 * Deleting is confirmed rather than undoable: it unpicks a label from every post
 * carrying it, which is not a row coming back the way a knowledge-base record
 * does.
 */
export function CampaignManager({
  campaigns,
  onCreate,
  onUpdate,
  onRemove,
  onDismiss,
}: CampaignManagerProps) {
  const [editing, setEditing] = useState<CampaignRead | 'new' | null>(null);
  const [doomed, setDoomed] = useState<CampaignRead | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);

  async function handleSubmit(body: CampaignCreate) {
    setIsSaving(true);
    setWriteError(null);
    try {
      if (editing && editing !== 'new') {
        await onUpdate(editing.id, body);
      } else {
        await onCreate(body);
      }
      setEditing(null);
    } catch (caught) {
      setWriteError(getErrorMessage(caught, 'That campaign did not save.'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(campaign: CampaignRead) {
    setIsSaving(true);
    setWriteError(null);
    try {
      await onRemove(campaign.id);
      setDoomed(null);
    } catch (caught) {
      setWriteError(getErrorMessage(caught, 'That campaign could not be deleted.'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <Modal title="Campaigns" kind="campaigns" onDismiss={onDismiss}>
        {campaigns.length === 0 && editing === null ? (
          <div className="kb-section__empty">
            <p className="kb-section__empty-text">
              No campaigns yet. Group a set of posts under one name and colour.
            </p>
            <button className="button" type="button" onClick={() => setEditing('new')}>
              Add campaign
            </button>
          </div>
        ) : null}

        {campaigns.length > 0 ? (
          <ul className="campaign-list">
            {campaigns.map((campaign) => (
              <li className="campaign-row" key={campaign.id}>
                <span
                  className="campaign-row__dot"
                  style={{ backgroundColor: campaign.color }}
                  aria-hidden="true"
                />
                <div className="campaign-row__body">
                  <span className="campaign-row__name">{campaign.name}</span>
                  {campaign.starts_on ? (
                    <span className="campaign-row__dates">
                      {formatDate(campaign.starts_on)}
                      {campaign.ends_on ? ` – ${formatDate(campaign.ends_on)}` : ''}
                    </span>
                  ) : null}
                </div>
                <div className="campaign-row__actions">
                  <button
                    className="button button--ghost"
                    type="button"
                    onClick={() => {
                      setWriteError(null);
                      setEditing(campaign);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="button button--ghost button--danger-text"
                    type="button"
                    onClick={() => {
                      setWriteError(null);
                      setDoomed(campaign);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        {editing === null ? (
          campaigns.length > 0 ? (
            <div className="kb-form__actions">
              <button className="button" type="button" onClick={() => setEditing('new')}>
                Add campaign
              </button>
            </div>
          ) : null
        ) : (
          <CampaignForm
            initial={editing === 'new' ? null : editing}
            isSaving={isSaving}
            error={writeError}
            onSubmit={(body) => void handleSubmit(body)}
            onCancel={() => {
              setWriteError(null);
              setEditing(null);
            }}
          />
        )}

        {writeError && editing === null && doomed === null ? (
          <p className="form__error" role="alert">
            {writeError}
          </p>
        ) : null}
      </Modal>

      {doomed ? (
        <ConfirmDialog
          title="Delete campaign"
          message={`"${doomed.name}" goes for good. Posts planned under it stay on the calendar without a campaign.`}
          confirmLabel="Delete campaign"
          busyLabel="Deleting"
          isBusy={isSaving}
          error={writeError}
          onConfirm={() => void handleDelete(doomed)}
          onDismiss={() => setDoomed(null)}
        />
      ) : null}
    </>
  );
}
